import { Routes } from '@angular/router';
import { PublicShell } from './shell/public-shell/public-shell';
import { AuthShell } from './shell/auth-shell/auth-shell';
import { mfaFlowGuard } from './core/guards/mfa-flow-guard';
import { authenticatedRootRedirectGuard } from './core/guards/authenticated-root-redirect.guard';
import { publicRegistrationGuard } from './core/guards/public-registration.guard';
import { subscriptionRequiredGuard } from './core/guards/subscription-required.guard';
import { platformAdminLoginRedirectGuard } from './core/guards/platform-admin-login-redirect.guard';

export const routes: Routes = [
  {
    path: 'privacy/identity-verification',
    title: 'Identity verification privacy notice | Apex Habitat',
    data: { notice: 'privacy' },
    loadComponent: () => import('./pages/identity-notices/identity-notices').then(m => m.IdentityNoticesComponent),
  },
  {
    path: 'verification/consent',
    title: 'Sandbox verification consent | Apex Habitat',
    data: { notice: 'consent' },
    loadComponent: () => import('./pages/identity-notices/identity-notices').then(m => m.IdentityNoticesComponent),
  },
  {
    path: 'verification',
    pathMatch: 'full',
    loadComponent: () => import('./pages/verification/verification').then(m => m.VerificationComponent),
  },
  {
    path: 'verification/return',
    loadComponent: () => import('./pages/verification-return/verification-return').then(m => m.VerificationReturnComponent),
  },
  {
    path: '',
    component: PublicShell,
    children: [
      {
        path: '',
        canActivate: [authenticatedRootRedirectGuard],
        loadComponent: () => import('./pages/landing/landing').then((m) => m.Landing),
      },
    ],
  },
  {
    path: '',
    component: AuthShell,
    children: [
      {
        path: 'login',
        loadComponent: () => import('./pages/login/login').then((m) => m.Login),
      },
      {
        path: 'platform/login',
        canActivate: [platformAdminLoginRedirectGuard],
        loadComponent: () => import('./pages/login/login').then((m) => m.Login),
      },
      {
        path: 'find-workspace',
        loadComponent: () => import('./pages/login/login').then((m) => m.Login),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./pages/forgot-password/forgot-password').then((m) => m.ForgotPassword),
      },
      {
        path: 'invitation-account-setup',
        loadComponent: () =>
          import('./pages/forgot-password/forgot-password').then((m) => m.ForgotPassword),
      },
      {
        path: 'forgot-password/new',
        loadComponent: () =>
          import('./pages/create-new-password/create-new-password').then(
            (m) => m.CreateNewPassword,
          ),
      },
      {
        path: 'owner-invitations/:token',
        loadComponent: () => import('./pages/owner-invitation/owner-invitation').then(m => m.OwnerInvitationComponent),
      },
      {
        path: 'invitations/:token',
        loadComponent: () =>
          import('./pages/owner-invitation/owner-invitation').then(
            (m) => m.OwnerInvitationComponent,
          ),
      },
      {
        path: 'tenant-invitations/:token',
        loadComponent: () =>
          import('./pages/tenant-invitation/tenant-invitation').then(
            (m) => m.TenantInvitationComponent,
          ),
      },
      {
        path: 'rental-applications/:token',
        loadComponent: () =>
          import('./pages/rental-application/rental-application').then(
            (m) => m.RentalApplicationComponent,
          ),
      },
      {
        path: 'screening-consent/:token',
        loadComponent: () =>
          import('./pages/screening-consent/screening-consent').then(
            (m) => m.ScreeningConsentComponent,
          ),
      },
      {
        path: 'team-invitations/:token',
        loadComponent: () =>
          import('./pages/team-invitation/team-invitation').then(
            (m) => m.TeamInvitationComponent,
          ),
      },
      {
        path: 'register',
        canActivate: [publicRegistrationGuard],
        loadComponent: () => import('./pages/register/register').then((m) => m.Register),
      },
      {
        path: 'register/details',
        canActivate: [publicRegistrationGuard],
        loadComponent: () =>
          import('./pages/register-details/register-details').then((m) => m.RegisterDetails),
      },
      {
        path: 'mfa/select',
        canActivate: [mfaFlowGuard],
        loadComponent: () =>
          import('./pages/mfa-method-select/mfa-method-select').then((m) => m.MfaMethodSelect),
      },
      {
        path: 'mfa/authenticator-setup',
        loadComponent: () =>
          import('./pages/authenticator-setup/authenticator-setup').then(
            (m) => m.AuthenticatorSetup,
          ),
      },
      {
        path: 'mfa/verify',
        canActivate: [mfaFlowGuard],
        loadComponent: () => import('./pages/mfa-verify/mfa-verify').then((m) => m.MfaVerify),
      },
      {
        path: 'organization/workspace-setup',
        redirectTo: 'organization/create',
        pathMatch: 'full',
      },
      {
        path: 'organization/create',
        canMatch: [subscriptionRequiredGuard],
        canActivate: [subscriptionRequiredGuard],
        loadComponent: () =>
          import('./pages/workspace-company-setup/workspace-company-setup').then(
            (m) => m.WorkspaceCompanySetupComponent,
          ),
      },
      {
        path: 'organization/workspace-contacts',
        canMatch: [subscriptionRequiredGuard],
        canActivate: [subscriptionRequiredGuard],
        loadComponent: () =>
          import('./pages/workspace-contacts-setup/workspace-contacts-setup').then(
            (m) => m.WorkspaceContactsSetupComponent,
          ),
      },
      {
        path: 'organization/properties/start',
        canMatch: [subscriptionRequiredGuard],
        canActivate: [subscriptionRequiredGuard],
        loadComponent: () =>
          import('./pages/property-onboarding-start/property-onboarding-start').then(
            (m) => m.PropertyOnboardingStartComponent,
          ),
      },
      {
        path: 'select-workspace',
        loadComponent: () =>
          import('./pages/workspace-select/workspace-select').then(
            (m) => m.WorkspaceSelectComponent,
          ),
      },
      {
        path: 'subscription/select',
        loadComponent: () =>
          import('./pages/subscription-select/subscription-select').then(
            (m) => m.SubscriptionSelectComponent,
          ),
      },
      {
        path: 'subscription/payment',
        loadComponent: () =>
          import('./pages/subscription-payment/subscription-payment').then(
            (m) => m.SubscriptionPayment,
          ),
      },
      {
        path: 'subscription/success',
        loadComponent: () =>
          import('./pages/subscription-success/subscription-success').then(
            (m) => m.SubscriptionSuccessComponent,
          ),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
